import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useReunion } from '../../contexts/ReunionContext';

export default function EmailComposer() {
  const { reunion } = useReunion();
  const [recipientType, setRecipientType] = useState<'all' | 'individual'>('all');
  const [recipientEmails, setRecipientEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  const sendMutation = useMutation({
    mutationFn: (data: {
      recipientType: 'all' | 'individual';
      recipientEmails?: string[];
      subject: string;
      htmlContent: string;
    }) => api.post('/admin/email/send', data),
    onSuccess: () => {
      setSent(true);
      setSubject('');
      setBody('');
      setRecipientEmails('');
    },
  });

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setSent(false);
    sendMutation.mutate({
      recipientType,
      recipientEmails:
        recipientType === 'individual'
          ? recipientEmails
              .split(',')
              .map((addr) => addr.trim())
              .filter(Boolean)
          : undefined,
      subject,
      htmlContent: `<p>${body.replace(/\n/g, '</p><p>')}</p>`,
    });
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Email Composer</h1>
      {reunion && (
        <p className="text-gray-600 mb-4">Sending as: {reunion.name}</p>
      )}

      {sent && (
        <div className="bg-green-50 border border-green-200 p-4 rounded mb-4">
          Email sent successfully.
        </div>
      )}

      <form onSubmit={handleSend} className="space-y-4">
        <div>
          <label className="block font-medium mb-1">Recipients</label>
          <select
            value={recipientType}
            onChange={(e) =>
              setRecipientType(e.target.value as 'all' | 'individual')
            }
            className="w-full border rounded p-2"
          >
            <option value="all">All Members</option>
            <option value="individual">Individual Emails</option>
          </select>
        </div>

        {recipientType === 'individual' && (
          <div>
            <label className="block font-medium mb-1">
              Email Addresses (comma-separated)
            </label>
            <input
              type="text"
              value={recipientEmails}
              onChange={(e) => setRecipientEmails(e.target.value)}
              className="w-full border rounded p-2"
              placeholder="email1@example.com, email2@example.com"
              required
            />
          </div>
        )}

        <div>
          <label className="block font-medium mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            className="w-full border rounded p-2"
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={8}
            className="w-full border rounded p-2"
          />
        </div>

        <button
          type="submit"
          disabled={sendMutation.isPending}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {sendMutation.isPending ? 'Sending...' : 'Send Email'}
        </button>

        {sendMutation.isError && (
          <div className="bg-red-50 border border-red-200 p-4 rounded">
            <p className="text-red-700 font-medium">Failed to send email</p>
            <p className="text-red-600 text-sm mt-1">
              {(sendMutation.error as any)?.message || 'Unknown error. Please try again.'}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
